import React, { useState, useMemo } from 'react';
import { Tag, Space, Button, Alert, Skeleton } from 'antd';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import * as Icons from '@ant-design/icons';
import type { CategoryInfo } from './CategoryTag';

export type CategoryTagCloudInfo = CategoryInfo & {
  count?: number; // カテゴリに属するアイテム数（オプション）
};

export interface CategoryTagCloudFilterProps {
  categories: CategoryTagCloudInfo[];
  selectedCategory?: string;
  onCategoryChange: (categoryId?: string) => void;
  maxVisibleTags?: number; // デフォルト: 10
  loading?: boolean;
  error?: Error | null;
  showCounts?: boolean; // カテゴリ件数を表示するか（デフォルト: true）
  showAllOption?: boolean; // 「すべて」オプションを表示するか（デフォルト: true）
  sortBy?: 'displayOrder' | 'count' | 'name'; // ソート順（デフォルト: displayOrder）
}

/**
 * カテゴリをタグクラウド形式で表示し、1クリックでフィルタリングできるコンポーネント
 *
 * 機能:
 * - カテゴリをタグ形式で横並びに表示
 * - 選択されたカテゴリはハイライト表示
 * - タグクリックで選択/解除（トグル動作）
 * - 「すべて」タグをクリックでフィルタ解除
 * - カテゴリ数が多い場合は展開/折りたたみ機能
 *
 * 使用例:
 * ```tsx
 * <CategoryTagCloudFilter
 *   categories={categories}
 *   selectedCategory={selectedCategory}
 *   onCategoryChange={setSelectedCategory}
 *   maxVisibleTags={10}
 *   showCounts={true}
 * />
 * ```
 */
export const CategoryTagCloudFilter: React.FC<CategoryTagCloudFilterProps> = React.memo(({
  categories,
  selectedCategory,
  onCategoryChange,
  maxVisibleTags = 10,
  loading = false,
  error = null,
  showCounts = true,
  showAllOption = true,
  sortBy = 'displayOrder',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // カテゴリのソート
  const sortedCategories = useMemo(() => {
    const sorted = [...categories];

    switch (sortBy) {
      case 'count':
        return sorted.sort((a, b) => (b.count || 0) - (a.count || 0));
      case 'name':
        return sorted.sort((a, b) => a.categoryLabel.localeCompare(b.categoryLabel, 'ja'));
      case 'displayOrder':
      default:
        // displayOrderはAPIから取得済みの順序を維持
        return sorted;
    }
  }, [categories, sortBy]);

  // 表示するカテゴリリスト
  const visibleCategories = useMemo(() => {
    if (isExpanded || sortedCategories.length <= maxVisibleTags) {
      return sortedCategories;
    }
    return sortedCategories.slice(0, maxVisibleTags);
  }, [sortedCategories, isExpanded, maxVisibleTags]);

  const hiddenCount = sortedCategories.length - maxVisibleTags;
  const shouldShowExpandButton = hiddenCount > 0;

  // アイコンコンポーネントを取得
  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const iconKey = iconName as keyof typeof Icons;
    const IconComponent = Icons[iconKey] as React.ComponentType<{ style?: React.CSSProperties }> | undefined;
    return IconComponent ? <IconComponent style={{ fontSize: 14 }} /> : null;
  };

  // タグクリックハンドラー
  const handleTagClick = (categoryId?: string) => {
    // 同じカテゴリをクリックした場合は選択解除
    if (categoryId === selectedCategory) {
      onCategoryChange(undefined);
    } else {
      onCategoryChange(categoryId);
    }
  };

  // キーボードナビゲーション
  const handleKeyDown = (e: React.KeyboardEvent, categoryId?: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleTagClick(categoryId);
    }
  };

  // ローディング状態
  if (loading) {
    return (
      <Space wrap size={8}>
        <Skeleton.Button active size="small" style={{ width: 60, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 80, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 70, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 90, height: 24 }} />
        <Skeleton.Button active size="small" style={{ width: 75, height: 24 }} />
      </Space>
    );
  }

  // エラー状態
  if (error) {
    return (
      <Alert
        type="error"
        message="カテゴリの読み込みに失敗しました"
        description="ページを再読み込みしてください。"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  // カテゴリが空の場合
  if (categories.length === 0) {
    return (
      <Alert
        type="info"
        message="カテゴリが登録されていません"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  return (
    <div style={{ marginBottom: 16 }} data-testid="equipment-category-tag-cloud">
      <Space wrap size={8}>
        {/* 「すべて」タグ */}
        {showAllOption && (
          <Tag
            color={!selectedCategory ? 'blue' : 'default'}
            style={{
              cursor: 'pointer',
              fontWeight: !selectedCategory ? 'bold' : 'normal',
              userSelect: 'none',
            }}
            onClick={() => handleTagClick(undefined)}
            onKeyDown={(e) => handleKeyDown(e, undefined)}
            role="button"
            tabIndex={0}
            aria-pressed={!selectedCategory}
            aria-label="すべてのカテゴリを表示"
          >
            すべて
          </Tag>
        )}

        {/* カテゴリタグ */}
        {visibleCategories.map((category) => {
          const isSelected = selectedCategory === category.id;
          const IconComponent = getIconComponent(category.icon);
          const displayText = showCounts && category.count !== undefined
            ? `${category.categoryLabel} (${category.count})`
            : category.categoryLabel;

          return (
            <Tag
              key={category.id}
              color={isSelected ? (category.color || 'blue') : 'default'}
              style={{
                cursor: 'pointer',
                fontWeight: isSelected ? 'bold' : 'normal',
                userSelect: 'none',
                borderColor: category.color || undefined,
              }}
              onClick={() => handleTagClick(category.id)}
              onKeyDown={(e) => handleKeyDown(e, category.id)}
              role="button"
              tabIndex={0}
              aria-pressed={isSelected}
              aria-label={`カテゴリフィルタ: ${category.categoryLabel}${showCounts && category.count !== undefined ? `, ${category.count}件` : ''}`}
            >
              {IconComponent && <span style={{ marginRight: 4 }}>{IconComponent}</span>}
              {displayText}
            </Tag>
          );
        })}

        {/* 展開/折りたたみボタン */}
        {shouldShowExpandButton && (
          <Button
            type="link"
            size="small"
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ padding: '0 8px', height: 24 }}
          >
            {isExpanded ? '折りたたむ' : `もっと見る (${hiddenCount}件)`}
          </Button>
        )}
      </Space>
    </div>
  );
});

CategoryTagCloudFilter.displayName = 'CategoryTagCloudFilter';
